/**
 * The full Create controls (formerly the Create tab), shown in the prompt bar's options popover: templates, genre,
 * mood, tempo, energy, instruments, variations, lyrics (with vocals) and an optional name. Stateless — the prompt bar
 * owns the form (`createForm.ts`) and passes it in.
 */
import type { ReactNode } from 'react';
import { cn } from '@/components/ui/utils';
import type { PresetOption, StudioPresets } from '@/api/audioStudioApi';
import { FALLBACK_PRESETS, applyTemplate, energyLabel, isTempo, type CreateFormState } from './createForm';

export const labelCls = 'mb-1.5 block text-[12px] text-[#344054]';
export const selectCls = 'w-full rounded-[12px] border border-[#eaecf0] bg-white px-3 py-2 text-[13px] text-[#344054] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[#98a2b3]/20 disabled:opacity-50';

export type SetField = <K extends keyof CreateFormState>(k: K, v: CreateFormState[K]) => void;

export function Segmented<T extends string | number>({ label, value, options, onChange, format, size = 'md', disabled }: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  format?: (v: T) => ReactNode;
  size?: 'sm' | 'md';
  disabled?: boolean;
}) {
  return (
    <div role="radiogroup" aria-label={label} className={cn('inline-flex rounded-[12px] bg-[#f2f4f7] p-0.5', size === 'md' && 'w-full', disabled && 'opacity-50')}>
      {options.map((o) => (
        <button
          key={String(o)}
          type="button"
          role="radio"
          aria-checked={value === o}
          disabled={disabled}
          onClick={() => onChange(o)}
          className={cn('flex-1 rounded-[10px] transition-colors', size === 'sm' ? 'px-2.5 py-1 text-[12px]' : 'px-3 py-1.5 text-[12.5px]', value === o ? 'bg-white text-[#101828] shadow-[0_1px_2px_rgba(16,24,40,0.06)]' : 'text-[#667085] hover:text-[#344054]')}
          style={{ fontWeight: 500 }}
        >
          {format ? format(o) : String(o)}
        </button>
      ))}
    </div>
  );
}

export function OptionSelect({ id, label, value, options, anyLabel, onChange }: { id: string; label: string; value: string; options: PresetOption[]; anyLabel: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls} style={{ fontWeight: 600 }}>{label}</label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
        <option value="">{anyLabel}</option>
        {options.map((o) => <option key={o.id} value={o.id} title={o.hint}>{o.label}</option>)}
        {value && !options.some((o) => o.id === value) && <option value={value}>{value}</option>}
      </select>
    </div>
  );
}

export function EnergySlider({ id, value, onChange }: { id: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <label htmlFor={id} className={labelCls} style={{ fontWeight: 600 }}>
        Energy <span className="ml-1 font-normal text-[#667085]">{energyLabel(value)} · {value}</span>
      </label>
      <input id={id} type="range" min={0} max={100} step={5} value={value} onChange={(e) => onChange(Number(e.target.value))} className="mt-2 w-full accent-[#475467]" />
    </div>
  );
}

export function CreateOptions({ form, set, setForm, presets }: {
  form: CreateFormState;
  set: SetField;
  setForm: (fn: (f: CreateFormState) => CreateFormState) => void;
  presets: StudioPresets;
}) {
  const templates = presets.templates || [];
  const tempoOptions = presets.tempos.length ? presets.tempos : FALLBACK_PRESETS.tempos;

  return (
    <div className="space-y-5">
      <fieldset className="space-y-5">
        <legend className="sr-only">Style</legend>
        {templates.length > 0 && (
          <div>
            <div className={labelCls} style={{ fontWeight: 600 }}>Start from a template</div>
            <div className="flex flex-wrap gap-2">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  title={tpl.description}
                  aria-pressed={form.presetId === tpl.id}
                  onClick={() => setForm((f) => (f.presetId === tpl.id ? { ...f, presetId: null } : applyTemplate(f, tpl)))}
                  className={cn('shrink-0 rounded-full border px-3 py-1.5 text-[12.5px] transition-colors', form.presetId === tpl.id ? 'border-[#344054] bg-[#f2f4f7] text-[#101828]' : 'border-[#eaecf0] bg-white text-[#344054] hover:bg-[#f9fafb]')}
                  style={{ fontWeight: 500 }}
                >
                  {tpl.emoji && <span className="mr-1" aria-hidden>{tpl.emoji}</span>}{tpl.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <OptionSelect id="audio-genre" label="Genre" value={form.genre} options={presets.genres} anyLabel="Any genre" onChange={(v) => set('genre', v)} />
          <OptionSelect id="audio-mood" label="Mood" value={form.mood} options={presets.moods} anyLabel="Any mood" onChange={(v) => set('mood', v)} />
          <OptionSelect id="audio-tempo" label="Tempo" value={form.tempo} options={tempoOptions} anyLabel="Any tempo" onChange={(v) => set('tempo', isTempo(v) ? v : '')} />
          <div className="sm:col-span-2">
            <EnergySlider id="audio-energy" value={form.energy} onChange={(v) => set('energy', v)} />
          </div>
        </div>

        <div>
          <div className={labelCls} style={{ fontWeight: 600 }}>Instruments</div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Instruments">
            {presets.instruments.map((o) => {
              const on = form.instruments.includes(o.id);
              return (
                <button key={o.id} type="button" aria-pressed={on} onClick={() => set('instruments', on ? form.instruments.filter((x) => x !== o.id) : [...form.instruments, o.id])} className={cn('rounded-full border px-2.5 py-1 text-[12px]', on ? 'border-[#344054] bg-[#f2f4f7] text-[#101828]' : 'border-[#eaecf0] bg-white text-[#475467] hover:bg-[#f9fafb]')}>
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {form.vocals && (
          <div>
            <label htmlFor="audio-lyrics" className={labelCls} style={{ fontWeight: 600 }}>Lyrics <span className="font-normal text-[#98a2b3]">(optional — we write them if empty)</span></label>
            <textarea id="audio-lyrics" value={form.lyrics} onChange={(e) => set('lyrics', e.target.value)} rows={4} maxLength={2000} placeholder={'[Verse]\nSun on my skin…\n[Chorus]\n…'} className="w-full rounded-[12px] border border-[#eaecf0] px-3 py-2 font-mono text-[12.5px] focus:border-[#d0d5dd] focus:outline-none focus:ring-2 focus:ring-[#98a2b3]/20" />
          </div>
        )}

        <div>
          <label htmlFor="audio-title" className={labelCls} style={{ fontWeight: 600 }}>Name <span className="font-normal text-[#98a2b3]">(optional)</span></label>
          <input id="audio-title" value={form.title} onChange={(e) => set('title', e.target.value)} maxLength={120} placeholder="We’ll name it for you" className={selectCls} />
        </div>
      </fieldset>

      <div>
        <div className={labelCls} style={{ fontWeight: 600 }}>Variations</div>
        <Segmented label="Variations" value={form.variations} options={[1, 2, 3] as const} onChange={(v) => set('variations', v)} />
      </div>
    </div>
  );
}
